import { useCallback, useEffect, useRef, useState } from 'react';
import { audioContext } from '@/audio/context';
import { Listener } from '@/speech/audio/listener';
import { openMicrophone, type AudioInput } from '@/speech/audio/sources';
import { SLM, TOPIC_MODEL } from '@/speech/understand/models';
import { sharedUnderstanding, type Answer } from '@/speech/understand/understanding';

export type VoiceState =
  | { kind: 'off' }
  | { kind: 'loading'; percent: number }
  | { kind: 'listening' }
  | { kind: 'thinking' }
  | { kind: 'error'; message: string };

/**
 * Answering by voice in the Tongue Lab: the child turns the microphone on (a tap: browsers need a
 * gesture), speech-to-text loads once, and every utterance's transcript goes to `onHeard`. The
 * microphone closes when the page is hidden (issue #68) or the lab closes. Tapping always works too.
 */
/** What the lab can ask about what the child said. */
export interface Understand {
  /** The approved answer to a question (keywords, or the language model once trusted). */
  answer: (question: string) => Promise<Answer>;
  /**
   * Which option of a question the child meant, by the classifier (the cross-encoder once it is
   * loaded; the language model only if it is turned on); null while neither is available.
   */
  choose: (
    question: string,
    options: readonly string[],
    text: string,
  ) => Promise<{ index: number | null; p: number } | null>;
}

export function useVoice(onHeard: (text: string, understand: Understand) => void | Promise<void>) {
  const [state, setState] = useState<VoiceState>({ kind: 'off' });
  const [heard, setHeard] = useState('');
  const live = useRef<{ listener: Listener; input: AudioInput } | null>(null);
  const handler = useRef(onHeard);
  useEffect(() => {
    handler.current = onHeard;
  }, [onHeard]);

  // One Understanding for the whole game (models load once); this hook follows its progress.
  const ensure = () => sharedUnderstanding();
  useEffect(
    () =>
      sharedUnderstanding().onProgress((part, loaded, total) => {
        if (part === 'stt' && total > 0)
          setState({ kind: 'loading', percent: Math.round((100 * loaded) / total) });
      }),
    [],
  );

  const stop = useCallback(async () => {
    const current = live.current;
    live.current = null;
    setState({ kind: 'off' });
    if (current) {
      await current.listener.close();
      current.input.close();
    }
  }, []);

  const start = useCallback(async () => {
    if (live.current) return;
    const u = ensure();
    try {
      const context = audioContext();
      if (context.state === 'suspended') await context.resume();
      await Listener.prepare(context);
      setState({ kind: 'loading', percent: 0 });
      const loading = u.load('stt');
      // The topic model after speech-to-text (one load at a time), ready for the first question.
      if (TOPIC_MODEL.use) void loading.then(() => u.load('topic')).catch(() => undefined);
      const input = await openMicrophone();
      const listener = await Listener.start(
        input.stream,
        {
          onUtterance: (utterance) => {
            setState({ kind: 'thinking' });
            u.transcribe(utterance.audio)
              .then(({ text }) => {
                setHeard(text);
                if (live.current) setState({ kind: 'listening' });
                if (text)
                  void handler.current(text, {
                    answer: (q) => u.answer(q),
                    choose: async (q, options, t) =>
                      u.canRank
                        ? u.rank(q, options, t).catch(() => null)
                        : SLM.use
                          ? u.choose(q, options, t).catch(() => null)
                          : null,
                  });
              })
              .catch(() => {
                if (live.current) setState({ kind: 'listening' });
              });
          },
        },
        context,
      );
      const session = { listener, input };
      live.current = session;
      await loading;
      // Still on (not stopped while speech-to-text loaded).
      if (live.current === session) setState({ kind: 'listening' });
    } catch (error) {
      await stop();
      setState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'The microphone did not start.',
      });
    }
  }, [stop]);

  // Release the microphone when the app is hidden or the lab closes.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) void stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      void stop();
    };
  }, [stop]);

  /** Approved answer to a question (keywords now; the language model once it is trusted). */
  const answer = useCallback((text: string) => ensure().answer(text), []);

  return { state, heard, start, stop, answer };
}
